import React, { useState, Suspense, lazy, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import VaultAuth from '../components/VaultAuth.jsx';
import PageNavigation from '../components/PageNavigation.jsx';
import { db } from '../lib/supabase.js';
import { ActivityLogger } from '../lib/activityLogger.js';

// Lazy load feature components
const FamilyGallery = lazy(() => import('./FamilyGallery.jsx'));
const LegacyDocuments = lazy(() => import('../components/LegacyDocuments.jsx'));
const TimeCapsuleVault = lazy(() => import('../components/TimeCapsuleVault.jsx'));
const LegacyEcho = lazy(() => import('../components/LegacyEcho.jsx'));
const InteractiveArtifacts = lazy(() => import('../components/InteractiveArtifacts.jsx'));

// Icon rendering helper
const renderVaultIcon = (iconName, size = 40) => {
  const color = '#c9a62b';
  const fill = 'rgba(201,166,43,0.2)';
  
  const icons = {
    'documents': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    'time-capsule': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M12 6v6l4 2" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="4" r="1" fill={color}/>
        <circle cx="20" cy="12" r="1" fill={color}/>
      </svg>
    ),
    'diary': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="2" width="16" height="20" rx="2" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M8 2v4M16 2v4M4 8h16M9 12h6M9 16h4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    'memory-journal': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L9 8H3L8 12L6 18L12 14L18 18L16 12L21 8H15L12 2Z" stroke={color} strokeWidth="1.5" fill={fill}/>
        <circle cx="12" cy="12" r="2" stroke={color} strokeWidth="1.5"/>
      </svg>
    ),
    'echo': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2a3 3 0 013 3v6a3 3 0 01-6 0V5a3 3 0 013-3z" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v3" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M5 7l2 2M19 7l-2 2" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      </svg>
    ),
    'artifacts': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="1.5" fill="none"/>
        <path d="M3 9h18M9 21V9" stroke={color} strokeWidth="1.5"/>
        <circle cx="6.5" cy="6" r="1.5" fill={fill} stroke={color}/>
      </svg>
    ),
    'albums': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke={color} strokeWidth="1.5"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M8 7h8M8 11h5" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      </svg>
    ),
    'profile': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="8" r="4" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    'stories': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M8 9h8M8 13h4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    'family-tree': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2v6M12 8a3 3 0 100 6 3 3 0 000-6z" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M9 14a3 3 0 10-6 0M15 14a3 3 0 106 0M12 14v4" stroke={color} strokeWidth="1.5"/>
        <path d="M6 18v4M18 18v4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    'milestones': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M12 6v6l3.5 3.5" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M6.5 17.5l3-3M17.5 6.5l-3 3" stroke={color} strokeWidth="1" opacity="0.5"/>
      </svg>
    ),
    'scrapbook': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L15 8.5L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L9 8.5L12 2Z" stroke={color} strokeWidth="1.5" fill={fill}/>
      </svg>
    ),
    'guestbook': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke={color} strokeWidth="1.5" fill={fill}/>
      </svg>
    ),
    'timeline': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 12h18M7 8V4M17 8V4M7 20v-4M17 20v-4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="7" cy="12" r="2" fill={fill} stroke={color} strokeWidth="1.5"/>
        <circle cx="17" cy="12" r="2" fill={fill} stroke={color} strokeWidth="1.5"/>
      </svg>
    ),
    'search': (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="11" cy="11" r="8" stroke={color} strokeWidth="1.5" fill={fill}/>
        <path d="M21 21l-4.35-4.35" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    )
  };
  
  return icons[iconName] || icons['documents'];
};

export default function VaultPage() {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [isVaultAuthenticated, setIsVaultAuthenticated] = useState(
    sessionStorage.getItem('jk_vault_authenticated') === 'true'
  );
  const [activeFeature, setActiveFeature] = useState('overview');
  const [showAuth, setShowAuth] = useState(false);
  
  // Diary state
  const [diaryEntries, setDiaryEntries] = useState([]);
  const [showDiaryForm, setShowDiaryForm] = useState(false);
  const [diaryForm, setDiaryForm] = useState({
    title: '',
    content: '',
    author: sessionStorage.getItem('jk_username') || '',
    mood: '',
    tags: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [diaryError, setDiaryError] = useState('');

  // Albums & Milestones state
  const [albums, setAlbums] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    albumName: '',
    eventType: '',
    date: '',
    description: '',
    familyMember: ''
  });

  // Load diary entries when diary feature is active
  useEffect(() => {
    if (activeFeature === 'diary' && isVaultAuthenticated) {
      loadDiaryEntries();
    }
  }, [activeFeature, isVaultAuthenticated]);

  // Scroll to content section when feature changes (not to top)
  useEffect(() => {
    if (activeFeature !== 'overview' && contentRef.current) {
      // Scroll to the feature content section
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeFeature]);

  const handleVaultAccess = () => {
    if (isVaultAuthenticated) {
      return;
    }
    setShowAuth(true);
  };

  const handleAuthSuccess = () => {
    setIsVaultAuthenticated(true);
    setShowAuth(false);
    
    // Log vault access
    ActivityLogger.vaultAccess(sessionStorage.getItem('jk_username') || 'unknown', 'face+totp');
  };

  const handleAuthCancel = () => {
    setShowAuth(false);
    navigate('/category/legacy-hub');
  };

  const handleFeatureClick = (featureId) => {
    setActiveFeature(featureId);
    
    // Log feature access
    const featureNames = {
      'gallery': 'Photo Gallery',
      'albums': 'Photo Albums',
      'profile': 'Family Profiles',
      'diary': 'Family Diary',
      'stories': 'Stories & Anecdotes',
      'family-tree': 'Family Tree',
      'milestones': 'Milestone Tracker',
      'audio': 'Audio Clips',
      'scrapbook': 'Digital Scrapbook',
      'guestbook': 'Memory Wall',
      'timeline': 'Timeline View',
      'search': 'Search & Filter'
    };
    ActivityLogger.featureAccess(featureNames[featureId] || featureId, featureId);
  };

  const handleBackToOverview = () => {
    setActiveFeature('overview');
  };

  // Diary functions
  const loadDiaryEntries = async () => {
    try {
      const entries = await db.getDiaryEntries();
      setDiaryEntries(entries || []);
    } catch (error) {
      console.error('Error loading diary entries:', error);
      setDiaryError('Failed to load diary entries');
    }
  };

  const handleNewEntry = () => {
    setDiaryForm({
      title: '',
      content: '',
      author: sessionStorage.getItem('jk_username') || '',
      mood: '',
      tags: []
    });
    setShowDiaryForm(true);
    setDiaryError('');
  };

  const handleDiaryFormChange = (field, value) => {
    setDiaryForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitDiary = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setDiaryError('');

    if (!diaryForm.title.trim() || !diaryForm.content.trim()) {
      setDiaryError('Title and content are required');
      setIsSubmitting(false);
      return;
    }

    try {
      await db.saveDiaryEntry(diaryForm);
      setShowDiaryForm(false);
      await loadDiaryEntries();
      
      // Log diary entry creation
      const tags = typeof diaryForm.tags === 'string' 
        ? diaryForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : diaryForm.tags;
      await ActivityLogger.diaryEntry(diaryForm.title, diaryForm.mood, tags);
      
      setDiaryForm({
        title: '',
        content: '',
        author: sessionStorage.getItem('jk_username') || '',
        mood: '',
        tags: []
      });
    } catch (error) {
      console.error('Error saving diary entry:', error);
      setDiaryError('Failed to save diary entry. Please try again.');
      
      // Log error
      await ActivityLogger.error('diary_save_error', error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Media Upload & Milestone Functions
  const handleMediaUpload = (e) => {
    const files = Array.from(e.target.files);
    const mediaFiles = files.map(file => ({
      file,
      url: URL.createObjectURL(file),
      type: file.type.startsWith('image') ? 'image' : 'video',
      name: file.name,
      size: file.size
    }));
    
    setUploadedMedia(mediaFiles);
  };

  const detectMilestoneFromMetadata = (formData) => {
    const { eventType, date, albumName, familyMember, description } = formData;
    
    // Auto-create milestone if event type is recognized
    const getMilestoneIcon = (type) => {
      const icons = {
        'birthday': <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9a62b"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="1.5"/><line x1="12" y1="8" x2="12" y2="2" strokeWidth="1.5"/><path d="M12 2c1.657 0 3 .672 3 1.5S13.657 5 12 5s-3-.672-3-1.5S10.343 2 12 2z" fill="#c9a62b"/></svg>,
        'anniversary': <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9a62b"><circle cx="12" cy="12" r="10" strokeWidth="1.5"/><path d="M12 5a7 7 0 1 1 0 14" strokeWidth="1.5"/></svg>,
        'graduation': <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9a62b"><path d="m2 7 10-4 10 4-10 4z" strokeWidth="1.5" fill="rgba(201,166,43,0.3)"/><path d="M6 11v6l6 3 6-3v-6" strokeWidth="1.5"/></svg>,
        'wedding': <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9a62b"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" strokeWidth="1.5" fill="rgba(201,166,43,0.2)"/></svg>,
        'achievement': <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9a62b"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18" strokeWidth="1.5"/><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="1.5" fill="rgba(201,166,43,0.2)"/></svg>,
        'vacation': <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9a62b"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" strokeWidth="1.5"/><polyline points="3.27 6.96 12 12.01 20.73 6.96" strokeWidth="1.5"/><line x1="12" y1="22.08" x2="12" y2="12" strokeWidth="1.5"/></svg>,
        'holiday': <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9a62b"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeWidth="1.5" fill="rgba(201,166,43,0.2)"/></svg>
      };
      return icons[type] || icons['birthday'];
    };

    const milestoneTypes = {
      'birthday': { category: 'Birthday' },
      'anniversary': { category: 'Anniversary' },
      'graduation': { category: 'Graduation' },
      'wedding': { category: 'Wedding' },
      'achievement': { category: 'Achievement' },
      'vacation': { category: 'Vacation' },
      'holiday': { category: 'Holiday' }
    };

    if (milestoneTypes[eventType]) {
      const milestone = {
        id: `milestone-${Date.now()}`,
        type: eventType,
        title: milestoneTypes[eventType].category,
        name: familyMember ? getFamilyMemberName(familyMember) : albumName,
        date: new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        description: description || albumName,
        albumName: albumName,
        createdAt: new Date().toISOString(),
        mediaCount: uploadedMedia.length
      };

      // Add to milestones
      setMilestones(prev => [...prev, milestone]);
      
      // Log milestone creation
      ActivityLogger.milestoneCreated(milestone.title, milestone.name, milestone.date);
      
      return milestone;
    }
    
    return null;
  };

  const getFamilyMemberName = (id) => {
    const names = {
      'jeyakumar-p': 'Jeyakumar',
      'viji-p': 'Viji',
      'rujai-ananth-j': 'Rujai Ananth',
      'prajit-kannan-j': 'Prajit Kannan',
      'thamoj-karthic-j': 'Thamoj Karthic'
    };
    return names[id] || 'Family';
  };

  const handleSaveUpload = async () => {
    if (!uploadForm.albumName || uploadedMedia.length === 0) {
      alert('Please provide an album name and select at least one file');
      return;
    }

    try {
      // Create album
      const newAlbum = {
        id: `album-${Date.now()}`,
        name: uploadForm.albumName,
        description: uploadForm.description,
        date: uploadForm.date ? new Date(uploadForm.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'No date',
        eventType: uploadForm.eventType,
        familyMember: uploadForm.familyMember,
        thumbnail: uploadedMedia[0]?.url,
        mediaCount: uploadedMedia.length,
        media: uploadedMedia,
        createdAt: new Date().toISOString()
      };

      // Add album to state
      setAlbums(prev => [newAlbum, ...prev]);

      // Detect and create milestone automatically
      if (uploadForm.eventType && uploadForm.date) {
        detectMilestoneFromMetadata(uploadForm);
      }

      // Log album creation
      await ActivityLogger.albumCreated(uploadForm.albumName, uploadedMedia.length);

      // Reset form
      setUploadForm({
        albumName: '',
        eventType: '',
        date: '',
        description: '',
        familyMember: ''
      });
      setUploadedMedia([]);
      setShowUploadForm(false);

      alert(`Album "${newAlbum.name}" created successfully!${uploadForm.eventType ? '\nMilestone automatically added!' : ''}`);
    } catch (error) {
      console.error('Error saving album:', error);
      alert('Failed to save album. Please try again.');
      await ActivityLogger.error('album_save_error', error.message);
    }
  };

  const vaultFeatures = [
    { id: 'gallery', name: 'Photo & Video Gallery', description: 'Family photos and videos' },
    { id: 'documents', name: 'Legacy Documents', description: 'Encrypted deeds, blueprints, trusts' },
    { id: 'audio', name: 'Voice Memos', description: 'Dad\'s voice & family recordings' },
    { id: 'time-capsule', name: 'Time Capsule', description: 'Snapshots from key milestones' },
    { id: 'diary', name: 'Family Diary', description: 'Daily journal and memories' },
    { id: 'memory-journal', name: 'AI Memory Journal', description: 'Auto-generated reflections & poetic notes' },
    { id: 'echo', name: 'Legacy Echo', description: 'AI memory prompts & wisdom' },
    { id: 'artifacts', name: 'Interactive Artifacts', description: 'Digital candle, crest, heirlooms' },
    { id: 'albums', name: 'Photo Albums', description: 'Organized by events, years, members' },
    { id: 'profile', name: 'Family Profiles', description: 'Member profiles and bios' },
    { id: 'stories', name: 'Stories & Anecdotes', description: 'Funny tales and memories' },
    { id: 'family-tree', name: 'Family Tree', description: 'Interactive genealogy' },
    { id: 'milestones', name: 'Milestone Tracker', description: 'Birthdays, anniversaries, achievements' },
    { id: 'scrapbook', name: 'Digital Scrapbook', description: 'Creative memory pages' },
    { id: 'guestbook', name: 'Memory Wall', description: 'Family notes and messages' },
    { id: 'timeline', name: 'Timeline View', description: 'Chronological journey' },
    { id: 'search', name: 'Search & Filter', description: 'Find memories quickly' }
  ];

  // Check if vault authentication is required
  if (!isVaultAuthenticated && showAuth) {
    return <VaultAuth onAuthenticated={handleAuthSuccess} onCancel={handleAuthCancel} />;
  }

  // If not authenticated, show lock screen
  if (!isVaultAuthenticated) {
    return (
      <div className="container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 500, padding: 40 }}>
          <div style={{ fontSize: 80, marginBottom: 24 }}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#c9a62b" style={{ display: 'block', margin: '0 auto' }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="1.5" fill="rgba(201,166,43,0.1)"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeWidth="1.5"/>
              <circle cx="12" cy="16" r="1.5" fill="#c9a62b"/>
            </svg>
          </div>
          <h1 style={{ color: '#c9a62b', fontSize: 48, marginBottom: 16 }}>Vault Locked</h1>
          <p style={{ color: '#bdbdbd', fontSize: 18, marginBottom: 32 }}>
            Additional security required to access your family's most precious memories and documents.
          </p>
          <button 
            className="btn auth-btn rounded-full" 
            onClick={handleVaultAccess}
            style={{ 
              borderRadius: '9999px', 
              padding: '16px 48px', 
              fontSize: 18,
              background: 'linear-gradient(135deg, #d4af37 0%, #c9a02b 100%)',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="1.5"/>
              <path d="M7 11V7a5 5 0 0 1 9.9-1" strokeWidth="1.5"/>
            </svg>
            Unlock Vault
          </button>
          <div style={{ marginTop: 24, color: '#888', fontSize: 14 }}>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeWidth="1.5"/>
                <polyline points="22 4 12 14.01 9 11.01" strokeWidth="1.5"/>
              </svg>
              Face Recognition Required
            </p>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeWidth="1.5"/>
                <polyline points="22 4 12 14.01 9 11.01" strokeWidth="1.5"/>
              </svg>
              TOTP Code Verification
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated - show vault content
  return (
    <div className="container" style={{ paddingTop: 80, paddingBottom: 80 }}>
      {/* Page Navigation */}
      <PageNavigation backTo="/category/legacy-hub" backLabel="← Back to Legacy Hub" />

      {/* Vault Header - Only show on overview page */}
      {activeFeature === 'overview' && (
        <div className="vault-header" style={{ textAlign: 'center', marginBottom: 48 }}>
          <h1 style={{ color: '#c9a62b', fontSize: 48, marginBottom: 12 }}>Family Vault</h1>
          <p style={{ color: '#bdbdbd', fontSize: 18 }}>
            Your most precious memories, secured and preserved for generations
          </p>
          <div style={{ marginTop: 16, padding: '8px 16px', background: 'rgba(201,166,43,0.1)', borderRadius: 8, display: 'inline-block' }}>
            <span style={{ color: '#c9a62b', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="20 6 9 17 4 12" strokeWidth="2"/>
              </svg>
              Vault Unlocked • Private & Encrypted
            </span>
          </div>
        </div>
      )}

      {/* Breadcrumb Navigation */}
      {activeFeature !== 'overview' && (
        <div style={{ marginBottom: 32, textAlign: 'left' }}>
          <button 
            className="btn rounded-full" 
            onClick={handleBackToOverview}
            style={{ 
              borderRadius: '9999px',
              background: 'rgba(201,166,43,0.1)',
              border: '1px solid rgba(201,166,43,0.3)',
              padding: '8px 24px',
              fontSize: 14
            }}
          >
            ← Back to All Features
          </button>
        </div>
      )}

      {/* Feature Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: 24, 
        marginBottom: 48,
        opacity: activeFeature === 'overview' ? 1 : 0,
        height: activeFeature === 'overview' ? 'auto' : 0,
        overflow: activeFeature === 'overview' ? 'visible' : 'hidden',
        transition: 'opacity 0.3s ease, height 0.3s ease'
      }}>
        {vaultFeatures.map(feature => (
          <div 
            key={feature.id}
            className="vault-feature-card"
            onClick={() => handleFeatureClick(feature.id)}
            style={{
              background: activeFeature === feature.id ? 'rgba(201,166,43,0.15)' : 'rgba(255,255,255,0.03)',
              border: activeFeature === feature.id ? '2px solid #c9a62b' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 16,
              padding: 24,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              transform: 'translateY(0)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.borderColor = '#c9a62b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = activeFeature === feature.id ? '#c9a62b' : 'rgba(255,255,255,0.1)';
            }}
          >
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
              {renderVaultIcon(feature.id, 40)}
            </div>
            <h3 style={{ color: '#c9a62b', fontSize: 20, marginBottom: 8 }}>{feature.name}</h3>
            <p style={{ color: '#bdbdbd', fontSize: 14, margin: 0 }}>{feature.description}</p>
          </div>
        ))}
      </div>

      {/* Feature Content */}
      <div ref={contentRef} className="vault-content" style={{ 
        background: 'rgba(255,255,255,0.02)', 
        borderRadius: 16, 
        border: '1px solid rgba(255,255,255,0.1)',
        padding: 40,
        minHeight: 400,
        opacity: activeFeature !== 'overview' ? 1 : 0,
        display: activeFeature !== 'overview' ? 'block' : 'none',
        animation: activeFeature !== 'overview' ? 'fadeIn 0.4s ease' : 'none'
      }}>
        {activeFeature === 'gallery' && (
          <Suspense fallback={<div style={{ color: '#bdbdbd', textAlign: 'center' }}>Loading gallery...</div>}>
            <FamilyGallery />
          </Suspense>
        )}

        {activeFeature === 'albums' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              {renderVaultIcon('albums', 48)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Photo & Video Albums</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Organize your memories by events, years, or family members.</p>
            
            {/* Upload Button */}
            <div style={{ marginBottom: 32 }}>
              <button 
                className="btn rounded-full" 
                style={{ borderRadius: '9999px', marginRight: 12, display: 'inline-flex', alignItems: 'center', gap: 8 }}
                onClick={() => setShowUploadForm(!showUploadForm)}
              >
                {showUploadForm ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round"/>
                      <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round"/>
                    </svg>
                    Cancel
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="17 8 12 3 7 8" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="12" y1="3" x2="12" y2="15" strokeLinecap="round"/>
                    </svg>
                    Upload Photos/Videos
                  </>
                )}
              </button>
            </div>

            {/* Upload Form */}
            {showUploadForm && (
              <div style={{
                background: 'rgba(201,166,43,0.05)',
                border: '2px solid rgba(201,166,43,0.2)',
                borderRadius: 16,
                padding: 32,
                marginBottom: 32
              }}>
                <h3 style={{ color: '#c9a62b', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c9a62b" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="17 8 12 3 7 8" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="12" y1="3" x2="12" y2="15" strokeLinecap="round"/>
                  </svg>
                  Upload New Media
                </h3>
                
                <div style={{ display: 'grid', gap: 16, marginBottom: 20 }}>
                  <div>
                    <label style={{ color: '#bdbdbd', fontSize: 14, display: 'block', marginBottom: 8 }}>Album Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Summer Vacation 2024"
                      value={uploadForm.albumName}
                      onChange={(e) => setUploadForm({...uploadForm, albumName: e.target.value})}
                      style={{
                        width: '100%',
                        padding: 12,
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <label style={{ color: '#bdbdbd', fontSize: 14, display: 'block', marginBottom: 8 }}>Event Type</label>
                      <select
                        value={uploadForm.eventType}
                        onChange={(e) => setUploadForm({...uploadForm, eventType: e.target.value})}
                        style={{
                          width: '100%',
                          padding: 12,
                          background: 'rgba(0,0,0,0.3)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 8,
                          color: '#fff',
                          fontSize: 14
                        }}
                      >
                        <option value="">Select Event Type</option>
                        <option value="birthday">Birthday</option>
                        <option value="anniversary">Anniversary</option>
                        <option value="graduation">Graduation</option>
                        <option value="wedding">Wedding</option>
                        <option value="vacation">Vacation</option>
                        <option value="holiday">Holiday</option>
                        <option value="achievement">Achievement</option>
                        <option value="family-gathering">Family Gathering</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ color: '#bdbdbd', fontSize: 14, display: 'block', marginBottom: 8 }}>Date</label>
                      <input
                        type="date"
                        value={uploadForm.date}
                        onChange={(e) => setUploadForm({...uploadForm, date: e.target.value})}
                        style={{
                          width: '100%',
                          padding: 12,
                          background: 'rgba(0,0,0,0.3)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 8,
                          color: '#fff',
                          fontSize: 14
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ color: '#bdbdbd', fontSize: 14, display: 'block', marginBottom: 8 }}>Family Member (Optional)</label>
                    <select
                      value={uploadForm.familyMember}
                      onChange={(e) => setUploadForm({...uploadForm, familyMember: e.target.value})}
                      style={{
                        width: '100%',
                        padding: 12,
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 14
                      }}
                    >
                      <option value="">All Family Members</option>
                      <option value="jeyakumar-p">Jeyakumar</option>
                      <option value="viji-p">Viji</option>
                      <option value="rujai-ananth-j">Rujai Ananth</option>
                      <option value="prajit-kannan-j">Prajit Kannan</option>
                      <option value="thamoj-karthic-j">Thamoj Karthic</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ color: '#bdbdbd', fontSize: 14, display: 'block', marginBottom: 8 }}>Description</label>
                    <textarea
                      placeholder="Brief description of the event or moment..."
                      value={uploadForm.description}
                      onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                      style={{
                        width: '100%',
                        padding: 12,
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 14,
                        minHeight: 80,
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ color: '#bdbdbd', fontSize: 14, display: 'block', marginBottom: 8 }}>Select Files (Photos/Videos)</label>
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={(e) => handleMediaUpload(e)}
                      style={{
                        width: '100%',
                        padding: 12,
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 14
                      }}
                    />
                    <p style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
                      Supported formats: JPG, PNG, GIF, MP4, MOV, WebM
                    </p>
                  </div>
                </div>

                <button 
                  className="btn rounded-full" 
                  style={{ borderRadius: '9999px', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  onClick={handleSaveUpload}
                  disabled={!uploadForm.albumName || uploadedMedia.length === 0}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="17 21 17 13 7 13 7 21" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="7 3 7 8 15 8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Save to Album
                </button>
              </div>
            )}
            
            {/* Albums Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 20 }}>
              {albums.length > 0 ? (
                albums.map(album => (
                  <div key={album.id} style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 12,
                    padding: 20,
                    border: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(201,166,43,0.1)';
                    e.currentTarget.style.borderColor = 'rgba(201,166,43,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  }}
                  >
                    <div style={{ 
                      width: '100%', 
                      height: 150, 
                      background: album.thumbnail ? `url(${album.thumbnail}) center/cover` : 'rgba(201,166,43,0.1)', 
                      borderRadius: 8, 
                      marginBottom: 12, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: 48 
                    }}>
                      {!album.thumbnail && (
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: '#c9a62b' }}>
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="1.5" />
                          <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="1.5" />
                          <polyline points="21 15 16 10 5 21" strokeWidth="1.5" />
                        </svg>
                      )}
                    </div>
                    <h4 style={{ color: '#c9a62b', margin: '0 0 8px 0' }}>{album.name}</h4>
                    <p style={{ color: '#888', fontSize: 14, margin: '0 0 8px 0' }}>{album.description}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#bdbdbd', fontSize: 12 }}>{album.mediaCount || 0} items</span>
                      <span style={{ color: '#888', fontSize: 12 }}>{album.date}</span>
                    </div>
                  </div>
                ))
              ) : (
                ['Family Gatherings 2024', 'Summer Vacation 2023', 'Birthday Celebrations', 'Holiday Memories', 'School Years', 'Wedding Albums'].map(album => (
                  <div key={album} style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 12,
                    padding: 20,
                    border: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer'
                  }}>
                    <div style={{ width: '100%', height: 150, background: 'rgba(201,166,43,0.1)', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: '#c9a62b' }}>
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="1.5" />
                        <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="1.5" />
                        <polyline points="21 15 16 10 5 21" strokeWidth="1.5" />
                      </svg>
                    </div>
                    <h4 style={{ color: '#c9a62b', margin: '0 0 8px 0' }}>{album}</h4>
                    <p style={{ color: '#888', fontSize: 14, margin: 0 }}>Upload photos to create this album</p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeFeature === 'profile' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              {renderVaultIcon('profile', 32)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Family Profiles</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Detailed profiles for each family member with photos, bios, and memories.</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20 }}>
              {[
                { id: 'jeyakumar-p', name: 'Jeyakumar', photo: '/Family Pics/jeyakumar.PNG' },
                { id: 'viji-p', name: 'Viji', photo: '/Family Pics/viji.JPG' },
                { id: 'rujai-ananth-j', name: 'Rujai Ananth', photo: '/Family Pics/rujaiananth.JPG' },
                { id: 'prajit-kannan-j', name: 'Prajit Kannan', photo: '/Family Pics/prajit.JPG' },
                { id: 'thamoj-karthic-j', name: 'Thamoj Karthic', photo: '/Family Pics/thamoj.jpeg' }
              ].map(member => (
                <div 
                  key={member.id} 
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 12,
                    padding: 20,
                    border: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(201,166,43,0.1)';
                    e.currentTarget.style.borderColor = 'rgba(201,166,43,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  }}
                  onClick={() => navigate(`/profile/${member.id}`)}
                >
                  <div style={{ 
                    width: 100, 
                    height: 100, 
                    borderRadius: '50%', 
                    background: 'rgba(201,166,43,0.2)', 
                    margin: '0 auto 12px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img 
                      src={member.photo}
                      alt={member.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        position: 'absolute'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c9a62b" style="display:block;margin:auto;"><circle cx="12" cy="8" r="4" stroke-width="1.5"/><path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" stroke-width="1.5"/></svg>';
                      }}
                    />
                  </div>
                  <h4 style={{ color: '#c9a62b', margin: '0 0 8px 0' }}>{member.name}</h4>
                  <p style={{ color: '#888', fontSize: 12 }}>View Profile</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeFeature === 'diary' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              {renderVaultIcon('diary', 32)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Family Diary</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Daily journal entries and personal reflections from family members.</p>
            
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
              <div style={{ marginBottom: 24 }}>
                <button 
                  className="btn rounded-full" 
                  onClick={handleNewEntry}
                  style={{ borderRadius: '9999px', display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeWidth="1.5"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeWidth="1.5"/>
                  </svg>
                  New Entry
                </button>
              </div>

              {/* New Entry Form Modal */}
              {showDiaryForm && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  padding: 20
                }}>
                  <div style={{
                    background: '#1a1a1a',
                    borderRadius: 16,
                    padding: 32,
                    maxWidth: 600,
                    width: '100%',
                    border: '1px solid rgba(201,166,43,0.3)',
                    maxHeight: '90vh',
                    overflow: 'auto'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c9a62b">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeWidth="1.5"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeWidth="1.5"/>
                      </svg>
                      <h3 style={{ color: '#c9a62b', margin: 0 }}>New Diary Entry</h3>
                    </div>
                    <form onSubmit={handleSubmitDiary}>
                      <div style={{ marginBottom: 16 }}>
                        <input
                          type="text"
                          placeholder="Entry title..."
                          value={diaryForm.title}
                          onChange={(e) => handleDiaryFormChange('title', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '9999px',
                            color: '#fff',
                            fontSize: 16
                          }}
                          required
                        />
                      </div>
                      
                      <div style={{ marginBottom: 16 }}>
                        <textarea
                          placeholder="Write your thoughts..."
                          value={diaryForm.content}
                          onChange={(e) => handleDiaryFormChange('content', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 12,
                            color: '#fff',
                            fontSize: 16,
                            minHeight: 200,
                            resize: 'vertical'
                          }}
                          required
                        />
                      </div>
                      
                      <div style={{ marginBottom: 16 }}>
                        <select
                          value={diaryForm.mood}
                          onChange={(e) => handleDiaryFormChange('mood', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '9999px',
                            color: diaryForm.mood ? '#fff' : '#888',
                            fontSize: 16
                          }}
                        >
                          <option value="">How are you feeling?</option>
                          <option value="Happy">Happy</option>
                          <option value="Peaceful">Peaceful</option>
                          <option value="Sad">Sad</option>
                          <option value="Frustrated">Frustrated</option>
                          <option value="Thoughtful">Thoughtful</option>
                          <option value="Grateful">Grateful</option>
                          <option value="Tired">Tired</option>
                          <option value="Confident">Confident</option>
                        </select>
                      </div>
                      
                      <div style={{ marginBottom: 24 }}>
                        <input
                          type="text"
                          placeholder="Tags (comma-separated, e.g. family, memories, celebration)"
                          value={diaryForm.tags}
                          onChange={(e) => handleDiaryFormChange('tags', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '9999px',
                            color: '#fff',
                            fontSize: 14
                          }}
                        />
                      </div>
                      
                      <div style={{ display: 'flex', gap: 12 }}>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn"
                          style={{ 
                            borderRadius: '9999px',
                            flex: 1,
                            opacity: isSubmitting ? 0.6 : 1,
                            cursor: isSubmitting ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {isSubmitting ? (
                            <>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ animation: 'spin 1s linear infinite' }}>
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeWidth="2"/>
                              </svg>
                              Saving...
                            </>
                          ) : (
                            <>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" strokeWidth="1.5"/>
                                <polyline points="17 21 17 13 7 13 7 21" strokeWidth="1.5"/>
                                <polyline points="7 3 7 8 15 8" strokeWidth="1.5"/>
                              </svg>
                              Save Entry
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDiaryForm(false)}
                          disabled={isSubmitting}
                          style={{
                            padding: '12px 24px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '9999px',
                            color: '#fff',
                            cursor: 'pointer',
                            opacity: isSubmitting ? 0.6 : 1
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {diaryError && (
                <div style={{ 
                  background: 'rgba(255,100,100,0.1)', 
                  border: '1px solid rgba(255,100,100,0.3)',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 24,
                  color: '#ff6b6b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="1.5"/>
                    <line x1="12" y1="8" x2="12" y2="12" strokeWidth="1.5"/>
                    <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2"/>
                  </svg>
                  {diaryError}
                </div>
              )}
              
              {diaryEntries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ display: 'block', margin: '0 auto', color: '#888' }}>
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeWidth="1.5"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <p>No diary entries yet. Click "New Entry" to create your first one!</p>
                </div>
              ) : (
                diaryEntries.map(entry => (
                  <div key={entry.id} style={{
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 12,
                    padding: 24,
                    marginBottom: 16,
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ color: '#c9a62b', fontWeight: 'bold' }}>{entry.author}</span>
                      <span style={{ color: '#888', fontSize: 14 }}>{formatDate(entry.created_at)}</span>
                    </div>
                    {entry.mood && (
                      <div style={{ marginBottom: 8, fontSize: 14 }}>
                        <span style={{ color: '#888' }}>Mood: </span>
                        <span style={{ color: '#c9a62b' }}>{entry.mood}</span>
                      </div>
                    )}
                    <h3 style={{ color: '#fff', margin: '0 0 8px 0' }}>{entry.title}</h3>
                    <p style={{ color: '#bdbdbd', margin: 0, whiteSpace: 'pre-wrap' }}>{entry.content}</p>
                    {entry.tags && entry.tags.length > 0 && (
                      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {entry.tags.map((tag, i) => (
                          <span key={i} style={{ 
                            padding: '4px 12px', 
                            background: 'rgba(201,166,43,0.2)', 
                            borderRadius: 12, 
                            fontSize: 12, 
                            color: '#c9a62b' 
                          }}>
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeFeature === 'stories' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              {renderVaultIcon('stories', 32)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Stories & Anecdotes</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Funny childhood tales, heartwarming moments, and cherished family stories.</p>
            
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
              <button className="btn rounded-full" style={{ borderRadius: '9999px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeWidth="1.5"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" strokeWidth="1.5"/>
                </svg>
                Add Story
              </button>
              
              <div style={{ display: 'grid', gap: 20 }}>
                {[
                  { title: 'The Great Kitchen Adventure', author: 'Prajit Kannan', category: 'Funny', excerpt: 'When Thamoj tried to cook for the first time...' },
                  { title: 'Grandpa\'s Wisdom', author: 'Jeyakumar', category: 'Heartwarming', excerpt: 'Life lessons that shaped our family values...' },
                  { title: 'Summer Mischief', author: 'Rujai Ananth', category: 'Childhood', excerpt: 'Those unforgettable summer vacations...' }
                ].map(story => (
                  <div key={story.title} style={{
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 12,
                    padding: 24,
                    border: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer'
                  }}>
                    <div style={{ display: 'inline-block', background: 'rgba(201,166,43,0.2)', padding: '4px 12px', borderRadius: 12, fontSize: 12, color: '#c9a62b', marginBottom: 12 }}>{story.category}</div>
                    <h3 style={{ color: '#fff', margin: '0 0 12px 0' }}>{story.title}</h3>
                    <p style={{ color: '#bdbdbd', fontSize: 14, marginBottom: 12 }}>{story.excerpt}</p>
                    <div style={{ color: '#888', fontSize: 12 }}>By {story.author}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeFeature === 'family-tree' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              {renderVaultIcon('family-tree', 48)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Interactive Family Tree</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Explore your family genealogy with clickable profiles and connections.</p>
            
            <div style={{ textAlign: 'center', padding: 60, background: 'rgba(201,166,43,0.05)', borderRadius: 12 }}>
              {renderVaultIcon('family-tree', 80)}
              <h3 style={{ color: '#c9a62b', marginBottom: 12, marginTop: 24 }}>Family Tree Visualization</h3>
              <p style={{ color: '#bdbdbd', marginBottom: 24 }}>Interactive genealogy chart coming soon...</p>
              <button className="btn rounded-full" style={{ borderRadius: '9999px', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {renderVaultIcon('family-tree', 20)}
                Build Tree
              </button>
            </div>
          </div>
        )}

        {activeFeature === 'milestones' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              {renderVaultIcon('milestones', 48)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Milestone Tracker</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Track birthdays, anniversaries, graduations, promotions, and achievements. Milestones are automatically created when you upload photos with event details!</p>
            
            {milestones.length > 0 && (
              <div style={{ 
                background: 'rgba(201,166,43,0.1)', 
                border: '1px solid rgba(201,166,43,0.3)', 
                borderRadius: 12, 
                padding: 16, 
                marginBottom: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c9a62b">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeWidth="1.5"/>
                </svg>
                <p style={{ color: '#c9a62b', margin: 0, fontSize: 14 }}>
                  <strong>{milestones.length}</strong> milestone{milestones.length > 1 ? 's' : ''} automatically created from your uploaded photos!
                </p>
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
              {/* Display auto-created milestones first */}
              {milestones.map((milestone) => (
                <div key={milestone.id} style={{
                  background: 'linear-gradient(135deg, rgba(201,166,43,0.15) 0%, rgba(201,166,43,0.05) 100%)',
                  borderRadius: 12,
                  padding: 24,
                  border: '2px solid rgba(201,166,43,0.3)',
                  position: 'relative'
                }}>
                  <div style={{ 
                    position: 'absolute', 
                    top: 12, 
                    right: 12, 
                    background: 'rgba(201,166,43,0.8)', 
                    color: '#000', 
                    padding: '4px 8px', 
                    borderRadius: 8, 
                    fontSize: 10, 
                    fontWeight: 'bold' 
                  }}>
                    AUTO-CREATED
                  </div>
                  <div style={{ fontSize: 40, marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                    {getMilestoneIcon(milestone.type)}
                  </div>
                  <h4 style={{ color: '#c9a62b', margin: '0 0 8px 0' }}>{milestone.title}</h4>
                  <p style={{ color: '#fff', fontSize: 16, marginBottom: 4 }}>{milestone.name}</p>
                  <p style={{ color: '#888', fontSize: 14, marginBottom: 8 }}>{milestone.date}</p>
                  {milestone.description && (
                    <p style={{ color: '#bdbdbd', fontSize: 13, marginBottom: 8 }}>{milestone.description}</p>
                  )}
                  {milestone.albumName && (
                    <div style={{ 
                      marginTop: 12, 
                      padding: '8px 12px', 
                      background: 'rgba(0,0,0,0.3)', 
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      {renderVaultIcon('albums', 16)}
                      <span style={{ color: '#c9a62b', fontSize: 13 }}>From album: {milestone.albumName}</span>
                    </div>
                  )}
                  {milestone.mediaCount && (
                    <div style={{ color: '#888', fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="1.5" />
                        <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="1.5" />
                        <polyline points="21 15 16 10 5 21" strokeWidth="1.5" />
                      </svg>
                      {milestone.mediaCount} photo{milestone.mediaCount > 1 ? 's' : ''}/video{milestone.mediaCount > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Sample milestones */}
              {[
                { type: 'birthday', title: 'Birthday', name: 'Rujai Ananth', date: 'December 15' },
                { type: 'anniversary', title: 'Anniversary', name: 'Jeyakumar & Viji', date: 'March 22' },
                { type: 'graduation', title: 'Graduation', name: 'Thamoj Karthic', date: 'May 2024' },
                { type: 'achievement', title: 'Achievement', name: 'Prajit Kannan', date: 'Promotion - Oct 2024' }
              ].map((milestone, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: 12,
                  padding: 24,
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <div style={{ fontSize: 40, marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                    {getMilestoneIcon(milestone.type)}
                  </div>
                  <h4 style={{ color: '#c9a62b', margin: '0 0 8px 0' }}>{milestone.title}</h4>
                  <p style={{ color: '#fff', fontSize: 16, marginBottom: 4 }}>{milestone.name}</p>
                  <p style={{ color: '#888', fontSize: 14, margin: 0 }}>{milestone.date}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeFeature === 'audio' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              {renderVaultIcon('audio', 32)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Audio Clips</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Voice messages, lullabies, wedding speeches, and precious audio memories.</p>
            
            <div style={{ maxWidth: 600, margin: '0 auto' }}>
              <button className="btn rounded-full" style={{ borderRadius: '9999px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" strokeWidth="1.5"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" strokeWidth="1.5"/>
                  <line x1="12" y1="19" x2="12" y2="23" strokeWidth="1.5"/>
                  <line x1="8" y1="23" x2="16" y2="23" strokeWidth="1.5"/>
                </svg>
                Record New
              </button>
              
              {[
                { title: 'Wedding Speech - Jeyakumar', duration: '3:45', date: 'March 22, 1984' },
                { title: 'Childhood Lullaby', duration: '2:30', date: 'July 10, 1990' },
                { title: 'Family Gathering Message', duration: '5:12', date: 'December 25, 2023' }
              ].map(audio => (
                <div key={audio.title} style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: 12,
                  padding: 20,
                  marginBottom: 16,
                  border: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16
                }}>
                  <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c9a62b">
                      <path d="M9 18V5l12-2v13" strokeWidth="1.5"/>
                      <circle cx="6" cy="18" r="3" strokeWidth="1.5"/>
                      <circle cx="18" cy="16" r="3" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ color: '#fff', margin: '0 0 4px 0' }}>{audio.title}</h4>
                    <p style={{ color: '#888', fontSize: 12, margin: 0 }}>{audio.date} • {audio.duration}</p>
                  </div>
                  <button style={{ background: 'rgba(201,166,43,0.2)', border: 'none', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="12" height="14" viewBox="0 0 12 14" fill="#c9a62b">
                      <path d="M0 0v14l12-7L0 0z"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeFeature === 'scrapbook' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              {renderVaultIcon('scrapbook', 48)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Digital Scrapbook</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Creative memory pages with themes, stickers, and borders.</p>
            
            <div style={{ textAlign: 'center', padding: 60, background: 'rgba(201,166,43,0.05)', borderRadius: 12 }}>
              {renderVaultIcon('scrapbook', 80)}
              <h3 style={{ color: '#c9a62b', marginBottom: 12, marginTop: 24 }}>Creative Scrapbook Pages</h3>
              <p style={{ color: '#bdbdbd', marginBottom: 24 }}>Design beautiful memory pages with photos, text, and decorations.</p>
              <button className="btn rounded-full" style={{ borderRadius: '9999px', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {renderVaultIcon('scrapbook', 20)}
                Create Page
              </button>
            </div>
          </div>
        )}

        {activeFeature === 'guestbook' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              {renderVaultIcon('guestbook', 32)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Memory Wall</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Leave heartfelt notes and messages for family members.</p>
            
            <div style={{ maxWidth: 700, margin: '0 auto' }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 24, marginBottom: 24, border: '1px solid rgba(255,255,255,0.1)' }}>
                <textarea 
                  placeholder="Share a memory, thought, or message..."
                  style={{
                    width: '100%',
                    minHeight: 100,
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12,
                    padding: 16,
                    color: '#fff',
                    fontSize: 16,
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <button className="btn rounded-full" style={{ borderRadius: '9999px', marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeWidth="1.5"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeWidth="1.5"/>
                  </svg>
                  Post Message
                </button>
              </div>
              
              {[
                { author: 'Viji', message: 'So grateful for our beautiful family. Every moment is a blessing.', time: '2 hours ago' },
                { author: 'Prajit Kannan', message: 'Remember when we used to play in the garden? Those were the days!', time: '1 day ago' },
                { author: 'Jeyakumar', message: 'Proud of how far we\'ve come as a family. Love you all.', time: '3 days ago' }
              ].map((message, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.02)',
                  borderRadius: 12,
                  padding: 20,
                  marginBottom: 16,
                  border: '1px solid rgba(255,255,255,0.08)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ color: '#c9a62b', fontWeight: 'bold' }}>{message.author}</span>
                    <span style={{ color: '#888', fontSize: 12 }}>{message.time}</span>
                  </div>
                  <p style={{ color: '#bdbdbd', margin: 0 }}>{message.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeFeature === 'timeline' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              {renderVaultIcon('timeline', 32)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Timeline View</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Watch your family's journey unfold chronologically.</p>
            
            <div style={{ maxWidth: 800, margin: '0 auto', position: 'relative' }}>
              <div style={{ position: 'absolute', left: 20, top: 0, bottom: 0, width: 2, background: 'rgba(201,166,43,0.3)' }}></div>
              
              {[
                { year: '1973', event: 'Jeyakumar born', description: 'Beginning of the legacy' },
                { year: '1984', event: 'Viji born', description: 'A new chapter' },
                { year: '2000', event: 'Rujai Ananth born', description: 'Next generation arrives' },
                { year: '2002', event: 'Prajit Kannan born', description: 'Family grows' },
                { year: '2004', event: 'Thamoj Karthic born', description: 'Completing the trio' },
                { year: '2025', event: 'JK Legacy Created', description: 'Preserving our story for generations' }
              ].map((item, i) => (
                <div key={i} style={{ position: 'relative', paddingLeft: 60, marginBottom: 40 }}>
                  <div style={{
                    position: 'absolute',
                    left: 11,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: '#c9a62b',
                    border: '3px solid #000'
                  }}></div>
                  <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 12,
                    padding: 20,
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div style={{ color: '#c9a62b', fontWeight: 'bold', marginBottom: 8 }}>{item.year}</div>
                    <h4 style={{ color: '#fff', margin: '0 0 8px 0' }}>{item.event}</h4>
                    <p style={{ color: '#bdbdbd', margin: 0 }}>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeFeature === 'search' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              {renderVaultIcon('search', 32)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0 }}>Search & Filter</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 32 }}>Find memories by person, event, date, or keyword.</p>
            
            <div style={{ maxWidth: 600, margin: '0 auto' }}>
              <div style={{ marginBottom: 32 }}>
                <input 
                  type="text"
                  placeholder="Search memories, names, events..."
                  className="rounded-full"
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '9999px',
                    color: '#fff',
                    fontSize: 16
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
                {['All', 'Photos', 'Videos', 'Audio', 'Stories', 'Diary', '2024', '2023', 'Family Events'].map(filter => (
                  <button key={filter} className="rounded-full" style={{
                    padding: '8px 20px',
                    background: 'rgba(201,166,43,0.1)',
                    border: '1px solid rgba(201,166,43,0.3)',
                    borderRadius: '9999px',
                    color: '#c9a62b',
                    fontSize: 14,
                    cursor: 'pointer'
                  }}>
                    {filter}
                  </button>
                ))}
              </div>
              
              <div style={{ textAlign: 'center', padding: 60, background: 'rgba(255,255,255,0.02)', borderRadius: 12 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>
                  {renderVaultIcon('search', 48)}
                </div>
                <p style={{ color: '#bdbdbd' }}>Start searching to find memories...</p>
              </div>
            </div>
          </div>
        )}

        {activeFeature === 'documents' && (
          <Suspense fallback={
            <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>
                {renderVaultIcon('documents', 48)}
              </div>
              <p>Loading documents...</p>
            </div>
          }>
            <LegacyDocuments />
          </Suspense>
        )}

        {activeFeature === 'time-capsule' && (
          <Suspense fallback={
            <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>
                {renderVaultIcon('time-capsule', 48)}
              </div>
              <p>Opening time capsule...</p>
            </div>
          }>
            <TimeCapsuleVault />
          </Suspense>
        )}

        {activeFeature === 'memory-journal' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              {renderVaultIcon('memory-journal', 32)}
              <h2 style={{ color: '#c9a62b', fontSize: 32, margin: 0, fontFamily: 'Arsenica' }}>AI Memory Journal</h2>
            </div>
            <p style={{ color: '#bdbdbd', marginBottom: 48, fontSize: 16, lineHeight: 1.6 }}>
              Automatically generated reflections that celebrate your family's journey. 
              Our AI finds meaningful patterns and creates poetic notes from your memories.
            </p>

            {/* Sample Reflections */}
            <div style={{ 
              display: 'grid', 
              gap: 24, 
              maxWidth: 900, 
              margin: '0 auto' 
            }}>
              {/* Anniversary Reflection */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(201,166,43,0.1) 0%, rgba(201,166,43,0.05) 100%)',
                borderRadius: 16,
                padding: 32,
                border: '1px solid rgba(201,166,43,0.2)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  position: 'absolute', 
                  top: 16, 
                  right: 16, 
                  fontSize: 12, 
                  color: '#888',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="1.5"/>
                    <line x1="16" y1="2" x2="16" y2="6" strokeWidth="1.5"/>
                    <line x1="8" y1="2" x2="8" y2="6" strokeWidth="1.5"/>
                    <line x1="3" y1="10" x2="21" y2="10" strokeWidth="1.5"/>
                  </svg>
                  Today
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 48, marginBottom: 16, textAlign: 'center' }}>🎊</div>
                  <h3 style={{ 
                    color: '#c9a62b', 
                    fontSize: 24, 
                    marginBottom: 16,
                    fontFamily: 'Arsenica',
                    textAlign: 'center'
                  }}>
                    A Decade of Memories
                  </h3>
                  <p style={{ 
                    color: '#e0e0e0', 
                    fontSize: 18, 
                    lineHeight: 1.8,
                    textAlign: 'center',
                    marginBottom: 24,
                    fontStyle: 'italic'
                  }}>
                    "Today marks 10 years since your first family trip to Ooty!"
                  </p>
                  <div style={{
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 16
                  }}>
                    <p style={{ color: '#bdbdbd', fontSize: 14, lineHeight: 1.8 }}>
                      Ten summers have bloomed and faded since that mountain breeze first touched your faces. 
                      The laughter echoes still in those pine-scented valleys. Time may weather all things, 
                      but some moments remain evergreen in the garden of memory. 🌲✨
                    </p>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{
                      width: 100,
                      height: 100,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                      border: '2px solid rgba(201,166,43,0.3)'
                    }}>
                      🏔️
                    </div>
                    <div style={{
                      width: 100,
                      height: 100,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                      border: '2px solid rgba(201,166,43,0.3)'
                    }}>
                      👨‍👩‍👧‍👦
                    </div>
                  </div>
                </div>
              </div>

              {/* Weekly Reflection */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(76,175,80,0.1) 0%, rgba(76,175,80,0.05) 100%)',
                borderRadius: 16,
                padding: 32,
                border: '1px solid rgba(76,175,80,0.2)',
                position: 'relative'
              }}>
                <div style={{ 
                  position: 'absolute', 
                  top: 16, 
                  right: 16, 
                  fontSize: 12, 
                  color: '#888',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" strokeWidth="1.5"/>
                    <path d="M8 2v20M16 2v20" strokeWidth="1.5"/>
                    <ellipse cx="12" cy="12" rx="10" ry="4" strokeWidth="1.5"/>
                  </svg>
                  This Week
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 48, marginBottom: 16, textAlign: 'center' }}>🏡</div>
                  <h3 style={{ 
                    color: '#4CAF50', 
                    fontSize: 24, 
                    marginBottom: 16,
                    fontFamily: 'Arsenica',
                    textAlign: 'center'
                  }}>
                    Home & Heritage
                  </h3>
                  <p style={{ 
                    color: '#e0e0e0', 
                    fontSize: 18, 
                    lineHeight: 1.8,
                    textAlign: 'center',
                    marginBottom: 24,
                    fontStyle: 'italic'
                  }}>
                    "You've added 12 new photos to the family vault this week"
                  </p>
                  <div style={{
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: 12,
                    padding: 20
                  }}>
                    <p style={{ color: '#bdbdbd', fontSize: 14, lineHeight: 1.8 }}>
                      Like ancient trees adding rings, your family story grows richer with each passing season. 
                      These captured moments become the roots that anchor generations yet to come. 
                      Every photograph is a seed of memory, planted in the soil of time. 🌳📸
                    </p>
                  </div>
                </div>
              </div>

              {/* Birthday Milestone */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(233,30,99,0.1) 0%, rgba(233,30,99,0.05) 100%)',
                borderRadius: 16,
                padding: 32,
                border: '1px solid rgba(233,30,99,0.2)',
                position: 'relative'
              }}>
                <div style={{ 
                  position: 'absolute', 
                  top: 16, 
                  right: 16, 
                  fontSize: 12, 
                  color: '#888',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="1.5"/>
                    <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="1.5"/>
                    <polyline points="21 15 16 10 5 21" strokeWidth="1.5"/>
                  </svg>
                  Upcoming
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 48, marginBottom: 16, textAlign: 'center' }}>🎉</div>
                  <h3 style={{ 
                    color: '#E91E63', 
                    fontSize: 24, 
                    marginBottom: 16,
                    fontFamily: 'Arsenica',
                    textAlign: 'center'
                  }}>
                    Celebration Ahead
                  </h3>
                  <p style={{ 
                    color: '#e0e0e0', 
                    fontSize: 18, 
                    lineHeight: 1.8,
                    textAlign: 'center',
                    marginBottom: 24,
                    fontStyle: 'italic'
                  }}>
                    "Thamoj's birthday is in 3 days! 🎈"
                  </p>
                  <div style={{
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: 12,
                    padding: 20
                  }}>
                    <p style={{ color: '#bdbdbd', fontSize: 14, lineHeight: 1.8 }}>
                      Another candle to light the cake, another year to celebrate the gift of presence. 
                      The calendar turns, but the love remains constant—like stars that shine through every season. 
                      May this birthday bring joy as bright as childhood laughter. 🎊💝
                    </p>
                  </div>
                </div>
              </div>

              {/* AI Generation Info */}
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                borderRadius: 12,
                padding: 24,
                border: '1px solid rgba(255,255,255,0.1)',
                marginTop: 32
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ fontSize: 32 }}>🤖</div>
                  <div>
                    <h4 style={{ color: '#c9a62b', fontSize: 18, marginBottom: 12, fontFamily: 'Arsenica' }}>
                      How It Works
                    </h4>
                    <ul style={{ color: '#bdbdbd', fontSize: 14, lineHeight: 1.8, paddingLeft: 20 }}>
                      <li>AI analyzes your vault activity and milestone dates</li>
                      <li>Generates poetic reflections for anniversaries and special moments</li>
                      <li>Combines photos, music suggestions, and meaningful quotes</li>
                      <li>New reflections appear daily/weekly based on your family's journey</li>
                      <li>All content is private and generated from your vault data only</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeFeature === 'echo' && (
          <Suspense fallback={
            <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
              <p>Loading legacy echo...</p>
            </div>
          }>
            <LegacyEcho />
          </Suspense>
        )}

        {activeFeature === 'artifacts' && (
          <Suspense fallback={
            <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
              <p>Loading artifacts...</p>
            </div>
          }>
            <InteractiveArtifacts />
          </Suspense>
        )}
      </div>

      {/* Private Sharing Info */}
      <div style={{ 
        marginTop: 48, 
        padding: 24, 
        background: 'rgba(201,166,43,0.05)', 
        borderRadius: 12, 
        border: '1px solid rgba(201,166,43,0.2)',
        textAlign: 'center'
      }}>
        <h3 style={{ color: '#c9a62b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c9a62b" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Private & Secure
        </h3>
        <p style={{ color: '#bdbdbd', margin: 0 }}>
          All vault content is encrypted and stored locally. Password-protected with invite-only access. 
          </p>
      </div>
    </div>
  );
}
